import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
// import { resetPasswordSchema } from "@shared/schema";
import { Lock, ArrowLeft, CheckCircle, Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import logger from '@/utils/logger';

// Password validation: 8+ chars, 1 uppercase, 1 number, 1 special char
const passwordValidation = z.string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character");

export const resetPasswordSchema = z.object({
  token: z.string(),
  password: passwordValidation,
});

// Extend the schema to include confirmPassword for form validation
const resetPasswordFormSchema = resetPasswordSchema.extend({
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordFormSchema>;

export default function ResetPassword() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { resetPassword, isResetPasswordPending } = useAuth();

  // Get key from URL params (the reset link uses 'key' parameter)
  const [location] = useLocation();
  const urlParams = new URLSearchParams(window.location.search);
  const key = urlParams.get('key');

  // Debug log for testing
  logger.log('Current location:', location);
  logger.log('URL search params:', window.location.search);
  logger.log('Extracted key:', key);

  // Helper function to check if password meets all requirements
  const checkPasswordRequirements = (password: string) => {
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
    };
  };

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: {
      token: key || "",
      password: "",
      confirmPassword: "",
    },
  });

  // Watch form values for real-time validation
  const watchedPassword = form.watch("password");
  const watchedConfirmPassword = form.watch("confirmPassword");
  const passwordRequirements = checkPasswordRequirements(watchedPassword || "");
  const allPasswordRequirementsMet = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = watchedPassword && watchedConfirmPassword && watchedPassword === watchedConfirmPassword;
  const isFormValid = allPasswordRequirementsMet && passwordsMatch;

  const onSubmit = (data: ResetPasswordFormData) => {
    // Only send token and password to the API (exclude confirmPassword)
    const resetData = {
      token: data.token,
      password: data.password,
    };
    resetPassword(resetData, {
      onSuccess: () => {
        setIsSubmitted(true);
        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      },
    });
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Password Reset Successful</h1>
          <p className="text-gray-600 mb-6">
            Your password has been successfully updated. You can now login with your new password.
          </p>
          <Button
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            onClick={() => window.location.assign('/login')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // For testing purposes, show the form even without key - remove this for production
  const showInvalidTokenMessage = false; // Set to true for production

  if (!key && showInvalidTokenMessage) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Invalid Reset Link</h1>
          <p className="text-gray-600 mb-6">
            This password reset link is invalid or has expired. Please request a new one.
          </p>
          <Link href="/forgot-password">
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
              Request New Reset Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Reset Your Password</h1>
          <p className="text-gray-600">
            Enter your new password below. Make sure it's strong and secure.
          </p>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Hidden token field */}
          <input type="hidden" {...form.register("token")} />

          {/* New Password */}
          <div className="space-y-2">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                {...form.register("password")}
                className="flex px-10 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-full h-12 bg-white border-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300 shadow-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {form.formState.errors.password && (
              <p className="text-sm text-red-600">{form.formState.errors.password.message}</p>
            )}
            
            {/* Password Requirements - Show when user starts typing and hide when all met */}
            {watchedPassword && watchedPassword.length > 0 && !allPasswordRequirementsMet && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs space-y-1">
                <p className="font-semibold text-gray-700 mb-2">Password must contain:</p>
                <div className={`flex items-center ${passwordRequirements.length ? 'text-green-600' : 'text-gray-600'}`}>
                  <span className="mr-2">{passwordRequirements.length ? '✓' : '•'}</span>
                  At least 8 characters
                </div>
                <div className={`flex items-center ${passwordRequirements.uppercase ? 'text-green-600' : 'text-gray-600'}`}>
                  <span className="mr-2">{passwordRequirements.uppercase ? '✓' : '•'}</span>
                  One uppercase letter
                </div>
                <div className={`flex items-center ${passwordRequirements.number ? 'text-green-600' : 'text-gray-600'}`}>
                  <span className="mr-2">{passwordRequirements.number ? '✓' : '•'}</span>
                  One number
                </div>
                <div className={`flex items-center ${passwordRequirements.special ? 'text-green-600' : 'text-gray-600'}`}>
                  <span className="mr-2">{passwordRequirements.special ? '✓' : '•'}</span>
                  One special character
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm New Password"
                {...form.register("confirmPassword")}
                className="flex px-10 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm w-full h-12 bg-white border-2 border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300 shadow-sm"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {form.formState.errors.confirmPassword && (
              <p className="text-sm text-red-600">{form.formState.errors.confirmPassword.message}</p>
            )}
            
            {/* Real-time password match validation */}
            {watchedConfirmPassword && watchedConfirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-sm text-red-600">Passwords don't match</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isResetPasswordPending || !isFormValid}
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold rounded-lg"
          >
            {isResetPasswordPending ? "Updating Password..." : "Update Password"}
          </Button>

          <div className="text-center">
            <button
              type="button"
              className="text-emerald-600 hover:underline cursor-pointer text-sm bg-transparent border-none p-0"
              onClick={() => window.location.assign('/login')}
            >
              <ArrowLeft className="inline mr-1 h-4 w-4" />
              Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}